import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuthContext } from "../../contexts/AuthContext";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../lib/firebase";
import AuthModal from "../../components/AuthModal";
import { AlertTriangle } from "lucide-react";
import { getUserSubscriptions, getDefaultRedirectPath } from "../../utils/subscriptionHelpers";

// Helper function to check Coursedee subscription
const checkCoursedeeSubscription = async (uid: string): Promise<boolean> => {
  try {
    const paymentsRef = collection(db, 'subscription_payments');
    const userDoc = await getDocs(query(paymentsRef, where('userId', '==', uid), where('status', 'in', ['approved', 'verified'])));
    
    // Check if user has any Coursedee package
    const hasCoursedee = userDoc.docs.some(doc => {
      const data = doc.data();
      return data.planName?.includes('Coursedee') || data.source === 'coursedee';
    });
    
    return hasCoursedee;
  } catch (error) {
    console.error('[LoginPage] Error checking Coursedee subscription:', error);
    return false;
  }
};

export default function LoginPage() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirectPathFromQuery = searchParams.get('redirect');
  const redirectPathFromState = location.state?.from;
  const redirectPath = redirectPathFromState || redirectPathFromQuery;
  const [showVerificationWarning, setShowVerificationWarning] = useState(false);

  // Coursedee = สมัครฟรี/เข้าระบบด้วย email ธรรมดา (ไม่บังคับยืนยันอีเมล)
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isCoursedee = hostname.includes('aiforyou');
  const authMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  
  // Extract plan from redirect path
  const getPlanFromRedirect = (redirect: string | null) => {
    if (!redirect) return undefined;
    const match = redirect.match(/plan=|%3Fplan=(free|basic|premium|pro|pro_plus)/);
    return match ? match[1] as 'free' | 'basic' | 'premium' : undefined;
  };
  
  const plan = getPlanFromRedirect(redirectPath);
  
  console.log('[LoginPage] redirectPath:', redirectPath, 'plan:', plan, 'from state:', redirectPathFromState, 'from query:', redirectPathFromQuery);

  useEffect(() => {
    const checkUserAccess = async () => {
      if (user) {
        console.log('[LoginPage] User logged in:', { uid: user.uid, email: user.email, role: user.role, emailVerified: user.emailVerified });
        
        // Check for Coursedee redirect first
        const loginRedirectData = localStorage.getItem('loginRedirect');
        if (loginRedirectData) {
          try {
            const redirectInfo = JSON.parse(loginRedirectData);
            if (redirectInfo.source === 'coursedee') {
              console.log('[LoginPage] Coursedee redirect detected');
              localStorage.removeItem('loginRedirect');
              
              // Check if user has Coursedee subscription
              const hasCoursedeeSubscription = await checkCoursedeeSubscription(user.uid);
              
              if (hasCoursedeeSubscription) {
                // Redirect to Coursedee Dashboard
                window.location.href = '/coursedee/dashboard';
              } else {
                // Redirect to Coursedee landing to purchase
                window.location.href = redirectInfo.returnTo || '/';
              }
              return;
            }
          } catch (err) {
            console.error('[LoginPage] Error parsing loginRedirect:', err);
          }
        }
        
        // Coursedee: ไม่บังคับยืนยันอีเมล → เข้าหน้า dashboard เลย (ซื้อคอร์สค่อยปลดล็อก AI)
        if (isCoursedee) {
          console.log('[LoginPage] Coursedee login - redirecting to dashboard');
          window.location.href = redirectPath || '/coursedee/dashboard';
          return;
        }

        // Skip all checks for admin
        const isAdmin = user.email === 'viitamint@gmail.com';

        if (!isAdmin) {
          // Check if email is verified (for email/password login)
          if (user.provider === 'password' && !user.emailVerified) {
            console.log('[LoginPage] Email not verified');
            setShowVerificationWarning(true);
            return;
          }
        } else {
          console.log('[LoginPage] Admin login - skipping all checks');
        }

        // User has verified email (or is admin) - allow access
        console.log('[LoginPage] Access granted - redirecting...');
        console.log('[LoginPage] Redirect path:', redirectPath);
        
        // Get user subscriptions
        const subscriptions = await getUserSubscriptions(user.uid);
        console.log('[LoginPage] User subscriptions:', subscriptions);
        
        // Smart redirect logic - use window.location.href for reliable redirect
        if (redirectPath) {
          console.log('[LoginPage] Has redirect parameter:', redirectPath);
          
          // If redirect from salepage
          if (redirectPath.includes('/salepage') || redirectPath === '/dashboard') {
            // /dashboard itself works without a subscription (freemium: draft for free, pay to publish)
            console.log('[LoginPage] Redirect from salepage detected → /dashboard');
            window.location.href = '/dashboard';
            return; // Exit early to prevent further execution
          }
          // If redirect from main site
          else {
            console.log('[LoginPage] Redirect from main site detected');
            if (subscriptions.hasCourse) {
              console.log('[LoginPage] ✓ Has course subscription → /instructor/dashboard');
              window.location.href = '/instructor/dashboard';
            } else {
              console.log('[LoginPage] ✗ No course subscription → /');
              window.location.href = '/';
            }
            return; // Exit early to prevent further execution
          }
        } else {
          // No redirect param - check what user has
          console.log('[LoginPage] No redirect parameter, using default logic');
          if (subscriptions.hasCourse) {
            console.log('[LoginPage] ✓ Has course subscription → /instructor/dashboard');
            window.location.href = '/instructor/dashboard';
          } else if (subscriptions.hasSalepage) {
            console.log('[LoginPage] ✓ Has salepage subscription → /dashboard');
            window.location.href = '/dashboard';
          } else {
            console.log('[LoginPage] ✗ No subscriptions → /');
            window.location.href = '/';
          }
        }
      }
    };

    checkUserAccess();
  }, [user, navigate, redirectPath]);

  // Show verification warning
  if (showVerificationWarning) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              กรุณายืนยันอีเมลของคุณ
            </h2>
            <p className="text-gray-600 mb-4">
              เราได้ส่งลิงก์ยืนยันไปที่อีเมลของคุณแล้ว
            </p>
            <p className="text-sm text-gray-500">
              กรุณาตรวจสอบอีเมลและคลิกลิงก์เพื่อยืนยันตัวตน
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            กลับหน้าหลัก
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthModal
      isOpen={true}
      onClose={() => navigate('/')}
      mode={authMode}
      signupSource={isCoursedee ? 'coursedee' : 'salepage'}
      plan={plan}
      redirectPath={redirectPath}
    />
  );
}
